/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpServersDelete } from "../funcs/mcpServersDelete.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteMcpServerRequest,
  DeleteMcpServerSecurity,
} from "../models/operations/deletemcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteMcpServerMutationVariables = {
  request: DeleteMcpServerRequest;
  security?: DeleteMcpServerSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteMcpServerMutationData = void;

export type DeleteMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteMcpServer mcpServers
 *
 * @remarks
 * Delete an MCP server. A toolset's own hosted server (its id equals the toolset id) is disabled instead, as turning MCP off on the toolset does, and stays listed with visibility disabled. Other servers, including other toolset-backed ones, are deleted.
 */
export function useDeleteMcpServerMutation(
  options?: MutationHookOptions<
    DeleteMcpServerMutationData,
    DeleteMcpServerMutationError,
    DeleteMcpServerMutationVariables
  >,
): UseMutationResult<
  DeleteMcpServerMutationData,
  DeleteMcpServerMutationError,
  DeleteMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteMcpServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteMcpServer(): MutationKey {
  return ["@gram/client", "mcpServers", "delete"];
}

export function buildDeleteMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteMcpServerMutationVariables,
  ) => Promise<DeleteMcpServerMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteMcpServer(),
    mutationFn: function deleteMcpServerMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteMcpServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpServersDelete(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
